// Scoped per lobby code so a browser can hold separate identities for
// different lobbies (e.g. two tabs open to two different games) without one
// reconnect token clobbering another.
function identityKey(lobbyCode: string): string {
  return `drawing-conclusions:identity:${lobbyCode}`;
}

export interface StoredIdentity {
  playerId: string;
  reconnectToken: string;
  name: string;
  color: string;
}

export function loadIdentity(lobbyCode: string): StoredIdentity | null {
  try {
    const raw = localStorage.getItem(identityKey(lobbyCode));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.playerId === "string" &&
      typeof parsed?.reconnectToken === "string" &&
      typeof parsed?.name === "string" &&
      typeof parsed?.color === "string"
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveIdentity(lobbyCode: string, identity: StoredIdentity): void {
  try {
    localStorage.setItem(identityKey(lobbyCode), JSON.stringify(identity));
  } catch {
    // localStorage may be unavailable (private browsing quirks); reconnection is best-effort.
  }
}

export function clearIdentity(lobbyCode: string): void {
  try {
    localStorage.removeItem(identityKey(lobbyCode));
  } catch {
    // ignore
  }
}

const AUDIO_STORAGE_KEY = "drawing-conclusions:audio";

export interface StoredAudioSettings {
  muted: boolean;
  volume: number;
}

export function loadAudioSettings(): StoredAudioSettings {
  try {
    const raw = localStorage.getItem(AUDIO_STORAGE_KEY);
    if (!raw) return { muted: false, volume: 0.7 };
    const parsed = JSON.parse(raw);
    return {
      muted: typeof parsed?.muted === "boolean" ? parsed.muted : false,
      volume: typeof parsed?.volume === "number" ? parsed.volume : 0.7,
    };
  } catch {
    return { muted: false, volume: 0.7 };
  }
}

export function saveAudioSettings(settings: StoredAudioSettings): void {
  try {
    localStorage.setItem(AUDIO_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}
